import moment from 'moment-timezone';
import { Entities, Periods, MetricDataPoint, MEASURES, Measure, getGroupingRanges } from 'cio/utils/metrics/common';
import { CalculatedMeasure, calculateMeasure, DELIVERY_METRICS_MEASURE_DEFINITIONS, MeasureType } from '../measure';
import { computed } from '@ember/object';
import Ember from 'ember';
import lodashSum from 'lodash.sum';
import { Grouping, groupTimestamps } from '../resolution';
import { tracked } from '@glimmer/tracking';
import { ActionType } from 'cio/models/action';
import { deliveryStatesAndActionsPerType } from 'cio/utils/action-info';
import { TimeRange } from 'cio/utils/time';

export const TOTAL_MEASURES: Record<Measure, string> = {
  attempted: 'totalAttempted',
  bounced: 'totalBounced',
  clicked: 'totalClicked',
  clicked_to_opened: 'totalClickedToOpened',
  converted: 'totalConverted',
  created: 'totalCreated',
  delivered: 'totalDelivered',
  failed: 'totalFailed',
  opened: 'totalOpened',
  sent: 'totalSent',
  spammed: 'totalSpammed',
  suppressed: 'totalSuppressed',
  undeliverable: 'totalUndeliverable',
  unsubscribed: 'totalUnsubscribed',
  topic_unsubscribed: 'totalTopicUnsubscribed',
};

export type UnderscoredMeasure =
  | '_attempted'
  | '_bounced'
  | '_clicked'
  | '_clicked_to_opened'
  | '_converted'
  | '_created'
  | '_delivered'
  | '_failed'
  | '_opened'
  | '_sent'
  | '_spammed'
  | '_suppressed'
  | '_undeliverable'
  | '_unsubscribed'
  | '_topic_unsubscribed';

export const UNDERSCORED_MEASURES: Record<Measure, UnderscoredMeasure> = {
  attempted: '_attempted',
  bounced: '_bounced',
  clicked: '_clicked',
  clicked_to_opened: '_clicked_to_opened',
  converted: '_converted',
  created: '_created',
  delivered: '_delivered',
  failed: '_failed',
  opened: '_opened',
  sent: '_sent',
  spammed: '_spammed',
  suppressed: '_suppressed',
  undeliverable: '_undeliverable',
  unsubscribed: '_unsubscribed',
  topic_unsubscribed: '_topic_unsubscribed',
};

export function computedMeasure(measure: Measure) {
  let measureDefinition = DELIVERY_METRICS_MEASURE_DEFINITIONS[measure];

  let dependentKeys: string[] = [];

  if (measureDefinition.isComputed) {
    dependentKeys = [...measureDefinition.dependentKeys];
  } else {
    dependentKeys = [measureDefinition.valueKey];
  }

  if (measureDefinition.denominator) {
    dependentKeys.push(measureDefinition.denominator);
  }

  return computed(
    ...dependentKeys.map((m: Measure) => UNDERSCORED_MEASURES[m]),
    'resolution',
    'grouping',
    'range',
    'actionType',
    'aggregate',
    'metrics',
    // @ts-ignore the definition for computed doesn't seem to like it when we try
    // and destructure the dependent keys
    function (this: Metric) {
      return calculateMeasure(
        measure as Measure,
        MeasureType.DeliveryMetrics,
        this.aggregate && this.metrics != null ? this.metrics : [this],
        this.rawMetrics,
        this.groupedMetrics,
        this.allTimeTotals as any,
      );
    },
  ) as Ember.ComputedProperty<CalculatedMeasure>;
}

export function summarizeMetrics(entityId: string, entity: Entities, actionType: ActionType | null, metrics: Metric[]) {
  let summedData: any = {
    entityId,
    entityType: entity,
    actionType,
    period: null,
    range: null,
    grouping: null,
    apiRequestRange: null,
    data: {},
    aggregate: true,
    metrics,
  };

  MEASURES.forEach((measure) => (summedData.data[measure] = []));

  if (metrics.length > 0) {
    // Copy over period, range, grouping and apiRequestRange to our summed entry.
    // This can be done from any of metrics, because they should all contain
    // the same data, so we'll just go for the first one
    let [firstMetric] = Object.values(metrics);
    summedData.period = firstMetric.period;
    summedData.range = firstMetric.range;
    summedData.apiRequestRange = firstMetric.apiRequestRange;
    summedData.grouping = firstMetric.grouping;
    summedData.resolution = firstMetric.resolution;
  }

  metrics.forEach((metric) => {
    MEASURES.forEach((measure) => {
      if (metric.actionType != null) {
        let type: ActionType =
          metric.actionType === 'split_randomized_action' ? 'email_action' : (metric.actionType as ActionType);
        let availableMeasures = deliveryStatesAndActionsPerType(metric.canConvert)[type] ?? [];

        if (measure !== 'created' && !availableMeasures.includes(measure)) {
          summedData.data[measure] = summedData.data[measure] ?? [];
          return;
        }
      }

      let metricMeasure = metric[UNDERSCORED_MEASURES[measure]];
      if (summedData.data[measure] == null || summedData.data[measure].length === 0) {
        summedData.data[measure] = metricMeasure ?? [];
        return;
      }

      summedData.data[measure] = summedData.data[measure].map((value: MetricDataPoint, index: number) => {
        let measureY = metricMeasure[index]?.y;

        if (value.y == null && measureY == null) {
          return {
            x: value.x,
            y: null,
          };
        }

        if (value.y == null) {
          return {
            x: value.x,
            y: measureY,
          };
        }

        if (measureY == null) {
          return { ...value };
        }

        return {
          x: value.x,
          y: value.y + measureY,
        };
      });
    });

    Object.entries(TOTAL_MEASURES).forEach(([measure, totalMeasure]: [Measure, string]) => {
      let metricMeasure = metric[measure]?.allTimeTotal;
      if (summedData.data[totalMeasure] == null) {
        summedData.data[totalMeasure] = metricMeasure ?? 0;
        return;
      }

      summedData.data[totalMeasure] = summedData.data[totalMeasure] + metricMeasure;
    });
  });

  return new Metric(summedData);
}

export class Metric {
  entityId: number | string;
  entityType: Entities;
  actionType: string | null;
  canConvert: boolean;

  aggregate = false;
  metrics: Metric[] | null = null;

  // "period" value for API
  period: Periods;
  // "grouping" selected in UI
  grouping: Grouping;
  // how data comes grouped from the API
  resolution: Periods;

  range: { start?: moment.Moment; end?: moment.Moment } | null;
  apiRequestRange: { start?: moment.Moment; end?: moment.Moment } | null;
  fetchedAt: number; // unix timestamp when this metric was received from the API

  environmentId?: number;
  campaignId?: number;
  newsletterId?: number;

  unsentDrafts = false;

  // These contain all the raw data returned from the API - this may include
  // more data than necessary, because historically we used to show rolling
  // averaged data, which required us to get more data from the API
  @tracked _attempted: MetricDataPoint[] = [];
  @tracked _bounced: MetricDataPoint[] = [];
  @tracked _clicked: MetricDataPoint[] = [];
  @tracked _clicked_to_opened: MetricDataPoint[] = [];
  @tracked _converted: MetricDataPoint[] = [];
  @tracked _created: MetricDataPoint[] = [];
  @tracked _delivered: MetricDataPoint[] = [];
  @tracked _failed: MetricDataPoint[] = [];
  @tracked _opened: MetricDataPoint[] = [];
  @tracked _sent: MetricDataPoint[] = [];
  @tracked _spammed: MetricDataPoint[] = [];
  @tracked _suppressed: MetricDataPoint[] = [];
  @tracked _undeliverable: MetricDataPoint[] = [];
  @tracked _unsubscribed: MetricDataPoint[] = [];
  @tracked _topic_unsubscribed: MetricDataPoint[] = [];

  @tracked allTimeTotals = {
    attempted: 0,
    bounced: 0,
    clicked: 0,
    clicked_to_opened: 0,
    converted: 0,
    created: 0,
    delivered: 0,
    failed: 0,
    opened: 0,
    sent: 0,
    spammed: 0,
    suppressed: 0,
    undeliverable: 0,
    unsubscribed: 0,
    topic_unsubscribed: 0,
  };

  @tracked subscriptionTopicMetrics: { [key: string]: any } | null = null;

  get perSubscriptionTopicMetrics() {
    if (!this.subscriptionTopicMetrics) {
      return [];
    }
    return Object.entries(this.subscriptionTopicMetrics)
      .map((metric: any) => ({
        subscriptionTopicId: metric[0],
        value: metric[1].topic_unsubscribed.reduce((acc: number, value: number) => acc + value, 0),
      }))
      .filter((el) => el.value !== 0);
  }

  get rawMetrics() {
    let metrics: Record<any, MetricDataPoint[]> = {};

    MEASURES.forEach((measure) => {
      metrics[measure] = this[UNDERSCORED_MEASURES[measure]];
    });

    return metrics;
  }

  get groupedMetrics() {
    if (this.range == null) {
      return {};
    }

    let metrics: Record<any, MetricDataPoint[]> = {};

    let startTimestamp = (this.range.start?.unix() ?? 0) * 1000;
    let endTimestamp = (this.range.end?.unix() ?? 0) * 1000;

    MEASURES.forEach((measure) => {
      let trimmedData = [...this[UNDERSCORED_MEASURES[measure]]].filter((p) => p.x >= startTimestamp);

      if (!this.rangeLengths || this.rangeLengths.length === 0) {
        metrics[measure] = trimmedData.filter((p) => p.x <= endTimestamp);
        return;
      }

      let trimmedValues = trimmedData.map((p) => p.y);

      let timestamps = groupTimestamps(this.range as TimeRange, this.grouping, 'Etc/GMT+4');

      metrics[measure] = this.rangeLengths.map((len: number, i: number) => {
        return {
          x: timestamps[i],
          y: lodashSum(trimmedValues.splice(0, len)),
        };
      });
    });

    return metrics;
  }

  @computed('resolution', 'grouping', 'range.{start,end}')
  get rangeLengths() {
    if (!this.range) {
      return [];
    }

    return getGroupingRanges(this.range, this.resolution, this.grouping, 'Etc/GMT+4');
  }

  @computedMeasure('attempted') attempted!: CalculatedMeasure;
  @computedMeasure('bounced') bounced!: CalculatedMeasure;
  @computedMeasure('clicked') clicked!: CalculatedMeasure;
  @computedMeasure('clicked_to_opened') clicked_to_opened!: CalculatedMeasure;
  @computedMeasure('converted') converted!: CalculatedMeasure;
  @computedMeasure('created') created!: CalculatedMeasure;
  @computedMeasure('delivered') delivered!: CalculatedMeasure;
  @computedMeasure('failed') failed!: CalculatedMeasure;
  @computedMeasure('opened') opened?: CalculatedMeasure;
  @computedMeasure('sent') sent!: CalculatedMeasure;
  @computedMeasure('spammed') spammed!: CalculatedMeasure;
  @computedMeasure('suppressed') suppressed!: CalculatedMeasure;
  @computedMeasure('undeliverable') undeliverable!: CalculatedMeasure;
  @computedMeasure('unsubscribed') unsubscribed!: CalculatedMeasure;
  @computedMeasure('topic_unsubscribed') topic_unsubscribed!: CalculatedMeasure;

  constructor(obj: any) {
    this.entityId = obj.entityId;
    this.entityType = obj.entityType;
    this.actionType = obj.actionType;
    this.period = obj.period;
    this.range = obj.range;
    this.resolution = obj.resolution;
    this.grouping = obj.grouping ? obj.grouping : obj.period === Periods.CUSTOM ? Periods.DAYS : obj.period;
    this.apiRequestRange = obj.apiRequestRange;

    this.unsentDrafts = obj.data?.unsentDrafts ?? false;

    if (obj.aggregate != null) {
      this.aggregate = obj.aggregate;
      this.metrics = obj.metrics;
    }

    this.canConvert = obj.canConvert ?? true;

    if (obj.actionType === 'split_randomized_action') {
      this.canConvert = true;
    }

    this.fetchedAt = Math.floor(Date.now() / 1000);

    if (obj.meta?.environmentId) {
      this.environmentId = obj.meta.environmentId;
    }

    if (obj.meta?.campaignId) {
      this.campaignId = obj.meta.campaignId;
    }

    if (obj.meta?.newsletterId) {
      this.newsletterId = obj.meta.newsletterId;
    }

    this.subscriptionTopicMetrics = obj?.data?.subscription_topic_metrics;

    MEASURES.forEach((measure) => {
      if (obj?.data?.[measure]) {
        this[UNDERSCORED_MEASURES[measure]] = obj.data[measure];
      }

      let totalAttr = TOTAL_MEASURES[measure];
      if (obj?.data?.[totalAttr]) {
        this.allTimeTotals[measure] = obj.data[totalAttr];
      }
    });
  }
}
